import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../AuthContext';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";

function Login() {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Force password change states
    const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);
    const [tempUserData, setTempUserData] = useState(null);
    const [passwordChangeData, setPasswordChangeData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const { login } = useContext(AuthContext);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handlePasswordChangeData = (e) => {
        const { name, value } = e.target;
        setPasswordChangeData({ ...passwordChangeData, [name]: value });
    };

    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const toggleNewPasswordVisibility = () => setShowNewPassword(!showNewPassword);
    const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

    // ✅ Password strength validation
    const validatePasswordStrength = (password) => {
        const minLength = 8;
        const hasLowercase = /[a-z]/.test(password);
        const hasUppercase = /[A-Z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password);

        return {
            isValid:
                password.length >= minLength &&
                hasLowercase &&
                hasUppercase &&
                hasNumbers &&
                hasSpecialChar,
            requirements: {
                minLength: password.length >= minLength,
                hasLowercase,
                hasUppercase,
                hasNumbers,
                hasSpecialChar
            }
        };
    };

    // ✅ Handle Login
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const requestData = {
                email_id: formData.email,
                password: String(formData.password).trim()
            };

            const response = await axios.post(
                'https://erpapi.socialpartners.in/api/auth/login',
                //'https://erpapi.socialpartners.in/api/auth/login',
                requestData,
                { headers: { 'Content-Type': 'application/json' } }
            );

            const data = response.data;
            console.log("Login response:", data);

            // If user needs to change password
            if (data.user.force_password_change) {
                setTempUserData(data);
                setShowForcePasswordChange(true);
                setLoading(false);
                return;
            }

            // ✅ Normal login flow
            login(
                data.user.id,
                data.accessToken,   // store access token
                data.refreshToken,  // store refresh token
                data.user.user_name,
                data.user.email_id,
                data?.companyname || '',
                data?.address1 || '',
                data?.address2 || '',
                data?.city || '',
                data?.zipCode || '',
                data?.phone || ''
            );

            console.log("Logged in Successfully");
            navigate('/');
            setFormData({ email: '', password: '' });

        } catch (error) {
            if (error.response) {
                setError(error.response.data.error);
                console.error(error.response.data.error);
                window.Lobibox.notify('error', {
                    pauseDelayOnHover: true,
                    size: 'mini',
                    rounded: true,
                    delayIndicator: false,
                    icon: 'bx bx-x-circle',
                    continueDelayOnInactiveTab: false,
                    position: 'top right',
                    msg: error.response.data.error
                });
            } else {
                setError('An error occurred. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // ✅ Force password change handler
    const handleForcePasswordChange = async (e) => {
        e.preventDefault();

        if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
            Swal.fire({
                icon: 'error',
                title: 'Password Mismatch',
                text: 'New password and confirm password do not match!'
            });
            return;
        }

        const passwordValidation = validatePasswordStrength(passwordChangeData.newPassword);
        if (!passwordValidation.isValid) {
            let errorMessage = 'Password must meet the following requirements:\n';
            if (!passwordValidation.requirements.minLength) errorMessage += '• At least 8 characters\n';
            if (!passwordValidation.requirements.hasLowercase) errorMessage += '• At least one lowercase letter\n';
            if (!passwordValidation.requirements.hasUppercase) errorMessage += '• At least one uppercase letter\n';
            if (!passwordValidation.requirements.hasNumbers) errorMessage += '• At least one number\n';
            if (!passwordValidation.requirements.hasSpecialChar) errorMessage += '• At least one special character\n';

            Swal.fire({ icon: 'error', title: 'Weak Password', text: errorMessage });
            return;
        }

        try {
            setLoading(true);

            const response = await axiosInstance.put(`/users/forcepassword/${tempUserData.user.id}`, {
                password: passwordChangeData.newPassword
            }); 

            const data = response.data.data;
            console.log(data)
            // ✅ Login with fresh token after password change
            login(
                data.user.id,
                data.accessToken,
                data.refreshToken,
                data.user.user_name,
                data.user.email_id,
                data?.companyname || '',
                data?.address1 || '',
                data?.address2 || '',
                data?.city || '',
                data?.zipCode || '',
                data?.phone || ''
            );

            await Swal.fire({
                icon: 'success',
                title: 'Password Changed Successfully!',
                text: 'Your password has been updated and you are now logged in.',
                timer: 2000,
                showConfirmButton: false
            });

            setShowForcePasswordChange(false);
            setTempUserData(null);
            setPasswordChangeData({ newPassword: '', confirmPassword: '' });
            setFormData({ email: '', password: '' });
            navigate('/');

        } catch (error) {
            console.error('Error changing password:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: error.response?.data?.message || 'Failed to change password. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    const cancelPasswordChange = () => {
        setShowForcePasswordChange(false);
        setTempUserData(null);
        setPasswordChangeData({ newPassword: '', confirmPassword: '' });
        setError(null);
    };

    return (
        <div>
            {/* Main Wrapper */}
            <div className="main-wrapper login-body">
                <div className="login-wrapper">
                    <div className="container">
                        <img className="img-fluid logo-dark mb-2 logo-color" src="assets/img/logo2.png" alt="Logo" />
                        <img className="img-fluid logo-light mb-2" src="assets/img/logo2-white.png" alt="Logo" />
                        <div className="loginbox">
                            <div className="login-right">
                                <div className="login-right-wrap">
                                    <h1>Login</h1>
                                    <p className="account-subtitle">Access to our dashboard</p>

                                    <form onSubmit={handleSubmit}>
                                        <div className="input-block mb-3">
                                            <label className="form-control-label">Email Address</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                        <div className="input-block mb-3">
                                            <label className="form-control-label">Password</label>
                                            <div className="pass-group">
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    className="form-control pass-input"
                                                    required
                                                    disabled={loading}
                                                />
                                                <span
                                                    onClick={togglePasswordVisibility}
                                                    className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} toggle-password`}
                                                    style={{ cursor: 'pointer' }}
                                                ></span>
                                            </div>
                                        </div>

                                        {error && (
                                            <div className="alert alert-danger mb-3">
                                                {error}
                                            </div>
                                        )}

                                        <div className="input-block mb-3">
                                            <div className="row">
                                                <div className="col-6">
                                                    <div className="form-check custom-checkbox">
                                                        <input type="checkbox" className="form-check-input" id="cb1" />
                                                        <label className="custom-control-label" htmlFor="cb1">Remember me</label>
                                                    </div>
                                                </div>
                                                <div className="col-6 text-end">
                                                    <a className="forgot-link" href="forgot-password.html">Forgot Password ?</a>
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            className="btn btn-lg btn-primary w-100"
                                            type="submit"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Logging in...
                                                </>
                                            ) : (
                                                'Login'
                                            )}
                                        </button>

                                        <div className="login-or">
                                            <span className="or-line"></span>
                                            <span className="span-or">or</span>
                                        </div>

                                        {/* Social Login */}
                                        <div className="social-login mb-3">
                                            <span>Login with</span>
                                            <a href="#" className="facebook"><i className="fab fa-facebook-f"></i></a>
                                            <a href="#" className="google"><i className="fab fa-google"></i></a>
                                        </div>
                                        {/* /Social Login */}

                                        <div className="text-center dont-have">
                                            Don't have an account yet? <a href="register.html">Register</a>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* /Main Wrapper */}

            {/* Force Password Change Modal */}
            <div className={`modal fade ${showForcePasswordChange ? 'show' : ''}`}
                id="force_password_change"
                tabIndex="-1"
                style={{ display: showForcePasswordChange ? 'block' : 'none' }}
                aria-hidden={!showForcePasswordChange}
                data-bs-backdrop="static"
                data-bs-keyboard="false">
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header bg-warning text-dark">
                            <h4 className="modal-title">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                Password Change Required
                            </h4>
                        </div>
                        <form onSubmit={handleForcePasswordChange}>
                            <div className="modal-body">
                                <div className="alert alert-warning">
                                    <i className="fas fa-shield-alt me-2"></i>
                                    <strong>Security Notice:</strong> You must change your password before accessing the system.
                                    This is required for new accounts or after a password reset.
                                </div>

                                <div className="row">
                                    <div className="col-md-12 mb-4">
                                        <div className="user-info bg-light p-3 rounded">
                                            <h6>User Information:</h6>
                                            <p className="mb-1"><strong>Name:</strong> {tempUserData?.user?.user_name}</p>
                                            <p className="mb-0"><strong>Email:</strong> {tempUserData?.user?.email_id}</p>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>New Password <span className="text-danger">*</span></label>
                                            <div className="pass-group">
                                                <input
                                                    type={showNewPassword ? "text" : "password"}
                                                    className="form-control"
                                                    name="newPassword"
                                                    value={passwordChangeData.newPassword}
                                                    onChange={handlePasswordChangeData}
                                                    placeholder="Enter new password"
                                                    required
                                                />
                                                <span
                                                    onClick={toggleNewPasswordVisibility}
                                                    className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'} toggle-password`}
                                                    style={{ cursor: 'pointer' }}
                                                ></span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Confirm New Password <span className="text-danger">*</span></label>
                                            <div className="pass-group">
                                                <input
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    className="form-control"
                                                    name="confirmPassword"
                                                    value={passwordChangeData.confirmPassword}
                                                    onChange={handlePasswordChangeData}
                                                    placeholder="Confirm new password"
                                                    required
                                                />
                                                <span
                                                    onClick={toggleConfirmPasswordVisibility}
                                                    className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} toggle-password`}
                                                    style={{ cursor: 'pointer' }}
                                                ></span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-12">
                                        <div className="password-requirements">
                                            <h6>Password Requirements:</h6>
                                            <ul className="list-unstyled">
                                                <li className={`${passwordChangeData.newPassword.length >= 8 ? 'text-success' : 'text-muted'}`}>
                                                    <i className={`fas ${passwordChangeData.newPassword.length >= 8 ? 'fa-check-circle' : 'fa-circle'} me-2`}></i>
                                                    At least 8 characters long
                                                </li>
                                                <li className={`${/[a-z]/.test(passwordChangeData.newPassword) ? 'text-success' : 'text-muted'}`}>
                                                    <i className={`fas ${/[a-z]/.test(passwordChangeData.newPassword) ? 'fa-check-circle' : 'fa-circle'} me-2`}></i>
                                                    At least one lowercase letter
                                                </li>
                                                <li className={`${/[A-Z]/.test(passwordChangeData.newPassword) ? 'text-success' : 'text-muted'}`}>
                                                    <i className={`fas ${/[A-Z]/.test(passwordChangeData.newPassword) ? 'fa-check-circle' : 'fa-circle'} me-2`}></i>
                                                    At least one uppercase letter
                                                </li>
                                                <li className={`${/\d/.test(passwordChangeData.newPassword) ? 'text-success' : 'text-muted'}`}>
                                                    <i className={`fas ${/\d/.test(passwordChangeData.newPassword) ? 'fa-check-circle' : 'fa-circle'} me-2`}></i>
                                                    At least one number
                                                </li>
                                                <li className={`${/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(passwordChangeData.newPassword) ? 'text-success' : 'text-muted'}`}>
                                                    <i className={`fas ${/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(passwordChangeData.newPassword) ? 'fa-check-circle' : 'fa-circle'} me-2`}></i>
                                                    At least one special character
                                                </li>
                                                <li className={`${passwordChangeData.newPassword === passwordChangeData.confirmPassword && passwordChangeData.newPassword !== '' ? 'text-success' : 'text-muted'}`}>
                                                    <i className={`fas ${passwordChangeData.newPassword === passwordChangeData.confirmPassword && passwordChangeData.newPassword !== '' ? 'fa-check-circle' : 'fa-circle'} me-2`}></i>
                                                    Passwords match
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    onClick={cancelPasswordChange}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading || !validatePasswordStrength(passwordChangeData.newPassword).isValid || passwordChangeData.newPassword !== passwordChangeData.confirmPassword}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Updating Password...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-key me-2"></i>Change Password & Login
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Modal backdrop */}
            {showForcePasswordChange && (
                <div className="modal-backdrop fade show"></div>
            )}
        </div>
    );
}

export default Login